import { Map } from 'immutable';
import React, { PureComponent, ReactNode, useContext } from 'react';
import ConnectionState from '../../services/rtc/ConnectionState';
import TrackState from '../../services/rtc/TrackState';
import { printDebugTree } from '../../utils/debugTree';
import dumpMediaStream, { MediaStreamDump } from '../../services/rtc/DeviceManager/dumpMediaStream';
import { WithoutMethods } from '../../utils/types';
import { ITimeoutWarning } from '../../services/rtc/constants';
import { EntityStringId } from 'app/types/entity';

export interface IMediaStatus {
  readonly audio: TrackState;
  readonly video: TrackState;
  readonly speaking: boolean;
}

export interface IPeerDebugDump extends Omit<WithoutMethods<Peer>, 'audioStream' | 'videoStream' | 'screenStream'> {
  readonly audioStream?: MediaStreamDump;
  readonly videoStream?: MediaStreamDump;
  readonly screenStream?: MediaStreamDump;
}

type PeerConstructorArgs = (
  Partial<Omit<Peer, 'toJSON'>> &  // Optional args
  Pick<Peer, 'id'>                 // Required args
);

export class Peer {
  public readonly id: EntityStringId;
  public readonly connectionState: ConnectionState;
  public readonly connectionError?: Error;
  public readonly media: IMediaStatus;
  public readonly audioStream?: MediaStream;
  public readonly videoStream?: MediaStream;
  public readonly screenStream?: MediaStream;
  public readonly timeoutWarning?: ITimeoutWarning;

  public constructor(other: PeerConstructorArgs) {
    this.id = other.id;
    this.connectionState = other.connectionState || ConnectionState.Off;
    this.connectionError = other.connectionError;
    this.media = other.media || { audio: TrackState.Off, video: TrackState.Off, speaking: false };
    this.audioStream = other.audioStream;
    this.videoStream = other.videoStream;
    this.screenStream = other.screenStream;
    this.timeoutWarning = other.timeoutWarning;
  }

  // Consider RTCPeerConnection connecting if it failed (usually
  // due to broken connection) or is off (usually when sdp exchange
  // failed) because we're always trying to reconnect.
  public get isConnecting(): boolean {
    return (
      this.connectionState === ConnectionState.Connecting ||
      this.connectionState === ConnectionState.Failed ||
      this.connectionState === ConnectionState.Off
    );
  }

  public toJSON(): IPeerDebugDump {
    const {
      id,
      connectionState,
      isConnecting,
      connectionError,
      media,
      audioStream,
      videoStream,
      screenStream,
      timeoutWarning,
    } = this;

    return {
      id,
      connectionState,
      connectionError,
      timeoutWarning,
      isConnecting,
      media,
      audioStream: audioStream && dumpMediaStream(audioStream),
      videoStream: videoStream && dumpMediaStream(videoStream),
      screenStream: screenStream && dumpMediaStream(screenStream),
    };
  }
}

interface IPeersControl {
  addPeer(peer: PeerConstructorArgs): void;
  removePeer(id: string): void;
  setPeerConnectionState(id: string, connectionState: ConnectionState): void;
  setPeerConnectionError(id: string, connectionError?: Error): void;
  setPeerVideoStream(id: string, stream: MediaStream): void;
  setPeerAudioStream(id: string, stream: MediaStream): void;
  setPeerScreenStream(id: string, stream: MediaStream): void;
  setPeerMediaStatus(id: string, media: IMediaStatus): void;
  setPeerTimeoutWarning(id: string, warning?: ITimeoutWarning): void;
}

const PeersContext = React.createContext<Map<string, Peer>>(Map());
const PeersControlContext = React.createContext<IPeersControl | undefined>(undefined);

export const GetPeers = PeersContext.Consumer;
export const SetPeers = PeersContext.Provider;
export const GetPeersControl = PeersControlContext.Consumer;

export const usePeers = () => useContext(PeersContext);
export const usePeersControl = () => useContext(PeersControlContext);

interface IPeersProviderProps {
  children: ReactNode;
}

interface IPeersProviderState {
  peers: Map<string, Peer>;
}

export class PeersProvider extends PureComponent<IPeersProviderProps, IPeersProviderState> {
  public constructor(props: IPeersProviderProps) {
    super(props);

    this.state = {
      peers: Map(),
    };
  }

  public componentDidUpdate(prevProps: IPeersProviderProps, prevState: IPeersProviderState) {
    printDebugTree('peers', prevState.peers, this.state.peers);
  }

  public render() {
    const {
      state: { peers },
      props: { children },
    } = this;

    return (
      <PeersControlContext.Provider value={this}>
        <PeersContext.Provider value={peers}>
          {children}
        </PeersContext.Provider>
      </PeersControlContext.Provider>
    );
  }

  public readonly addPeer = (peer: PeerConstructorArgs) => {
    this.setState(({ peers }) => ({ peers: peers.set(peer.id, new Peer(peer)) }));
  };

  public readonly removePeer = (id: string) => {
    this.setState(({ peers }) => ({ peers: peers.delete(id) }));
  };

  public readonly setPeerConnectionState = (id: string, connectionState: ConnectionState) => {
    this.updatePeer(id, 'connectionState', connectionState);
  };

  public readonly setPeerConnectionError = (id: string, connectionError?: Error) => {
    this.updatePeer(id, 'connectionError', connectionError);
  };

  public readonly setPeerAudioStream = (id: string, stream: MediaStream) => {
    this.updatePeer(id, 'audioStream', stream);
  };

  public readonly setPeerVideoStream = (id: string, stream: MediaStream) => {
    this.updatePeer(id, 'videoStream', stream);
  };

  public readonly setPeerScreenStream = (id: string, stream: MediaStream) => {
    this.updatePeer(id, 'screenStream', stream);
  };

  public readonly setPeerMediaStatus = (id: string, media: IMediaStatus) => {
    this.updatePeer(id, 'media', media);
  };

  public readonly setPeerTimeoutWarning = (id: string, timeoutWarning: ITimeoutWarning) => {
    this.updatePeer(id, 'timeoutWarning', timeoutWarning);
  };

  private updatePeer<Field extends keyof Peer>(id: string, field: Field, value: Peer[Field]) {
    this.setState(({ peers }) =>
      peers.has(id)
      ? { peers: peers.update(id, (peer) => new Peer({ ...peer, [field]: value })) }
      : null,
    );
  }
}
